/**
 * @name NewsDialog
 * @description Modal de todos los avisos de la empresa
 * @author Ricardo Guerra
 */

import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { DataGrid, FiltersExpansion } from '@components';
import { Flex, Icons, MUI, Typography } from '@styles';

const NewsDialog = (props) => {
  const {
    news,
    filters,
    open,
    onClose,
    onFilter,
    onClickRow
  } = props;

  const columns = [
    { id: 'IdAvisoGeneral', label: i18n.t('GenericMessages.IdAvisoGeneral'), visible: false, filter: null },
    { id: 'Id', label: i18n.t('GenericMessages.Id'), visible: false, filter: null },
    // { id: 'URLArchivo', label: i18n.t('UploadFile.attachedImage'), visible: false, filter: 'DownloadFileFilter', minWidth: 250 },
    {
      type: 'render', id: 'URLArchivo', label: i18n.t('UploadFile.attachedImage'), visible: true, sort: true, minWidth: 220,
      render: (value) => (
        (value) ? (
          <Styled.CardImage>
            <Styled.Image src={value} />
          </Styled.CardImage>
        ) : (
          <Styled.CardImageDefault>
            <Styled.ImageDefault src={'https://assets.talentry.io/icons/default/news-logo.png'} />
          </Styled.CardImageDefault>
        )
      )
    },
    {
      type: 'render', id: 'Titulo', visible: true, filter: null, minWidth: 250,
      label: (
        <Flex.Row>
          <Typography.Medium>{i18n.t('News.title')}</Typography.Medium>
          <MUI.Divider orientation="vertical" margin="0 16px" style={{ height: '18px' }} />
          <Typography.Medium>{i18n.t('News.message')}</Typography.Medium>
        </Flex.Row>
      ),
      render: (value, row) => (
        <Flex.Col>
          <Typography.Medium>{value}</Typography.Medium>
          <Typography.Text>{row.Mensaje}</Typography.Text>
        </Flex.Col>
      )
    },
    { id: 'Leido', label: i18n.t('GenericMessages.read'), visible: true, filter: 'YesNoFilter', width: '8%' },
    { id: 'FechaInicio', label: i18n.t('GenericMessages.startDate'), visible: true, filter: 'DayNameDateFilter', width: '12%' },
    { id: 'FechaFin', label: i18n.t('GenericMessages.expirationDate'), visible: true, filter: 'DayNameDateFilter', width: '15%' },
  ];

  return (
    <MUI.Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
    >
      <MUI.DialogTitle>
        {`${i18n.t('News.news')}`}
        <MUI.DialogBoxClose onClick={onClose}>
          <Icons.Close />
        </MUI.DialogBoxClose>
      </MUI.DialogTitle>
      <MUI.DialogContent>
        <FiltersExpansion>
          <MUI.Grid container>
            <MUI.Grid
              item
              md={3}
              sm={6}
              xs={12}
            >
              <BoxFilter>
                <MUI.TextFieldIcon
                  select
                  displayEmpty
                  label={i18n.t('GenericMessages.status')}
                  type="text"
                  name="read"
                  value={filters.read}
                  InputProps={{ startAdornment: (<Icons.FilterList />) }}
                  onChange={(event) => onFilter({ read: event.target.value })}
                >
                  <MUI.SelectOption value={true}>
                    {i18n.t('GenericMessages.read')}
                  </MUI.SelectOption>
                  <MUI.SelectOption value={false}>
                    {i18n.t('GenericMessages.unread')}
                  </MUI.SelectOption>
                  <MUI.SelectOption value={'null'}>
                    {i18n.t('GenericMessages.all')}
                  </MUI.SelectOption>
                </MUI.TextFieldIcon>
              </BoxFilter>
            </MUI.Grid>
          </MUI.Grid>
        </FiltersExpansion>
        <MUI.Box padding="16px">
          <DataGrid
            columns={columns}
            rows={news}
            isPaginated={true}
            canSort={true}
            canSelect={true}
            onClickRow={onClickRow}
          />
        </MUI.Box>
      </MUI.DialogContent>
      <MUI.DialogActions>
        <MUI.ButtonText onClick={onClose}>
          {i18n.t('DialogConfirm.close')}
        </MUI.ButtonText>
      </MUI.DialogActions>
    </MUI.Dialog>
  );
};

NewsDialog.propTypes = {
  // Array con la información de los avisos de la empresa.
  news: PropTypes.array.isRequired,
  // Filtros de avisos.
  filters: PropTypes.object.isRequired,
  // Boolean que indica si el modal esta abierto.
  open: PropTypes.bool.isRequired,
  // Función que se ejecuta para cerrar el modal.
  onClose: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se aplica un filtro.
  onFilter: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se selecciona un aviso.
  onClickRow: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

const BoxFilter = styled.div`
  padding: 8px 16px;
`;

Styled.CardImage = styled.div`
  width: 100%;
  height: 100px;
  border-radius: 6px;
`;

Styled.Image = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 6px;
`;

Styled.CardImageDefault = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.greyLight};
  background-color: ${(props) => props.theme.colors.greyHover};
`;

Styled.ImageDefault = styled.img`
  height: 60px;
  width: auto;
  object-fit: cover;
  object-position: center;
`;

export default NewsDialog;